/**
 * API Client - Handles all HTTP requests with JWT authentication
 * Location: src/services/apiClient.js
 *
 * This client automatically includes the JWT token in all requests
 * and provides a clean interface for API calls.
 */

import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const apiClient = {
  /**
   * Get headers for API requests
   * Automatically includes JWT token if user is authenticated
   */
  getHeaders: (includeAuth = true) => {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = authService.getToken();
      if (token) {
        // 🔑 This is the critical line - includes JWT in Authorization header
        headers['Authorization'] = `Bearer ${token}`;
        console.log('[apiClient] ✅ JWT token included in headers');
      } else {
        console.warn('[apiClient] ⚠️ No JWT token found - request will likely fail');
      }
    }

    return headers;
  },

  /**
   * POST request with authentication
   * @param {string} endpoint - The API endpoint (e.g., '/external/jobs')
   * @param {object} data - The request body
   * @returns {Promise} Response data
   */
  post: async (endpoint, data) => {
    try {
      console.log(`[apiClient] 📤 POST ${endpoint}`, data);

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: apiClient.getHeaders(true), // 🔑 Include JWT token
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error(
          `[apiClient] ❌ POST ${endpoint} failed with status ${response.status}:`,
          responseData
        );
        throw new Error(responseData.message || `Request failed with status ${response.status}`);
      }

      console.log(`[apiClient] ✅ POST ${endpoint} successful`, responseData);
      return responseData;
    } catch (error) {
      console.error(`[apiClient] ❌ POST ${endpoint} error:`, error);
      throw error;
    }
  },

  /**
   * GET request with authentication
   * @param {string} endpoint - The API endpoint
   * @returns {Promise} Response data
   */
  get: async (endpoint) => {
    try {
      console.log(`[apiClient] 📥 GET ${endpoint}`);

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: apiClient.getHeaders(true), // 🔑 Include JWT token
      });

      const data = await response.json();

      if (!response.ok) {
        console.error(
          `[apiClient] ❌ GET ${endpoint} failed with status ${response.status}:`,
          data
        );
        throw new Error(data.message || `Request failed with status ${response.status}`);
      }

      console.log(`[apiClient] ✅ GET ${endpoint} successful`, data);
      return data;
    } catch (error) {
      console.error(`[apiClient] ❌ GET ${endpoint} error:`, error);
      throw error;
    }
  },

  /**
   * PUT request with authentication
   * @param {string} endpoint - The API endpoint
   * @param {object} data - The request body
   * @returns {Promise} Response data
   */
  put: async (endpoint, data) => {
    try {
      console.log(`[apiClient] 📤 PUT ${endpoint}`, data);

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: apiClient.getHeaders(true), // 🔑 Include JWT token
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error(
          `[apiClient] ❌ PUT ${endpoint} failed with status ${response.status}:`,
          responseData
        );
        throw new Error(responseData.message || `Request failed with status ${response.status}`);
      }

      console.log(`[apiClient] ✅ PUT ${endpoint} successful`, responseData);
      return responseData;
    } catch (error) {
      console.error(`[apiClient] ❌ PUT ${endpoint} error:`, error);
      throw error;
    }
  },

  /**
   * DELETE request with authentication
   * @param {string} endpoint - The API endpoint
   * @returns {Promise} Response data
   */
  delete: async (endpoint) => {
    try {
      console.log(`[apiClient] 🗑️ DELETE ${endpoint}`);

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: apiClient.getHeaders(true), // 🔑 Include JWT token
      });

      const data = await response.json();

      if (!response.ok) {
        console.error(
          `[apiClient] ❌ DELETE ${endpoint} failed with status ${response.status}:`,
          data
        );
        throw new Error(data.message || `Request failed with status ${response.status}`);
      }

      console.log(`[apiClient] ✅ DELETE ${endpoint} successful`, data);
      return data;
    } catch (error) {
      console.error(`[apiClient] ❌ DELETE ${endpoint} error:`, error);
      throw error;
    }
  },

  /**
   * PATCH request with authentication
   * @param {string} endpoint - The API endpoint
   * @param {object} data - The request body (partial data for partial updates)
   * @returns {Promise} Response data
   */
  patch: async (endpoint, data) => {
    try {
      console.log(`[apiClient] 🔧 PATCH ${endpoint}`, data);

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PATCH',
        headers: apiClient.getHeaders(true), // 🔑 Include JWT token
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error(
          `[apiClient] ❌ PATCH ${endpoint} failed with status ${response.status}:`,
          responseData
        );
        throw new Error(responseData.message || `Request failed with status ${response.status}`);
      }

      console.log(`[apiClient] ✅ PATCH ${endpoint} successful`, responseData);
      return responseData;
    } catch (error) {
      console.error(`[apiClient] ❌ PATCH ${endpoint} error:`, error);
      throw error;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if JWT token exists
   */
  isAuthenticated: () => {
    return authService.isAuthenticated();
  },

  /**
   * Get current JWT token
   * @returns {string|null} JWT token or null
   */
  getToken: () => {
    return authService.getToken();
  },
};

export default apiClient;